import React from 'react';
import { ClipboardList, Download, X } from 'lucide-react';
import ClaimStatusBadge from './ClaimStatusBadge';
import ClaimStatusHistory from './ClaimStatusHistory';
import ClaimWorksheetPanel from './ClaimWorksheetPanel';
import { ClaimsService } from '../../../services/billing/claims.service';
import { ALLOWED_NEXT_STATUSES, CORRECTION_BY_STATUS, VOIDABLE_STATUSES } from '../../../dtos/billing/claims.dto';
import type { ClaimDetail } from '../../../dtos/billing/claims.dto';

interface ClaimDetailPanelProps {
  claim: ClaimDetail;
  canUpdate: boolean;
  canVoid: boolean;
  canCorrect: boolean;
  onClose: () => void;
  onUpdateStatus: () => void;
  onVoid: () => void;
  onCorrect: () => void;
  onDownload: () => void;
  onOpenVersion: (claimId: string) => void;
}

const FLAG_LABELS: Record<string, string> = {
  EVV_OVERRIDE_UNVERIFIED: 'EVV override of a visit HHAX never verified',
  EVV_OVERRIDE: 'EVV minutes overridden by an admin'
};

/** Claim detail: lines, payer, PA, flags, version chain and status history (REQ-BIL-10/11). */
const ClaimDetailPanel: React.FC<ClaimDetailPanelProps> = ({
  claim, canUpdate, canVoid, canCorrect, onClose, onUpdateStatus, onVoid, onCorrect, onDownload, onOpenVersion
}) => {
  const canChangeStatus = canUpdate && (ALLOWED_NEXT_STATUSES[claim.status]?.length ?? 0) > 0;
  const voidable = canVoid && VOIDABLE_STATUSES.includes(claim.status);
  const correctable = canCorrect && (CORRECTION_BY_STATUS[claim.status]?.length ?? 0) > 0;
  const [showWorksheet, setShowWorksheet] = React.useState(false);
  const form = claim.claimForm || claim.formType || 'CMS1500';
  const formLabel = form === 'UB04' ? 'UB-04' : 'CMS-1500';

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4 text-left" aria-label="Claim detail" data-testid="claim-detail">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="text-base font-semibold text-slate-800">
            {claim.patientName} <span className="font-normal text-slate-500">· MRN {claim.mrn}</span>
          </h3>
          <p className="text-sm text-slate-600">
            {claim.payer?.payerName} · DOS {ClaimsService.formatDate(claim.dosFrom)}–{ClaimsService.formatDate(claim.dosTo)}
            {claim.sequenceCount && claim.sequenceCount > 1 ? ` · claim ${claim.sequence} of ${claim.sequenceCount}` : ''}
          </p>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
            <ClaimStatusBadge status={claim.status} />
            <span className="rounded bg-slate-100 px-1.5 py-0.5 font-medium text-slate-700" data-testid="claim-form-badge">{formLabel}{claim.typeOfBill ? ` · TOB ${claim.typeOfBill}` : ''}</span>
            <span>Version {claim.version || 1}{claim.frequencyCode && claim.frequencyCode !== '1' ? ` (frequency ${claim.frequencyCode})` : ''}</span>
            {claim.authNumber && <span>Auth # {claim.authNumber}</span>}
            {claim.payerClaimNumber && <span>ICN {claim.payerClaimNumber}</span>}
          </div>
        </div>
        <button type="button" onClick={onClose} aria-label="Close claim detail" className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
      </div>

      {(claim.flags?.length ?? 0) > 0 && (
        <ul className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-2 text-sm text-amber-900" aria-label="Claim flags">
          {claim.flags!.map((f) => <li key={`${f.code}-${f.visitId}`}>{FLAG_LABELS[f.code] || f.code}</li>)}
        </ul>
      )}

      <div className="mt-3 overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="text-xs uppercase text-slate-500">
            <tr><th className="py-1 pr-3 text-left">DOS</th>{form === 'UB04' && <th className="pr-3 text-left">Rev</th>}<th className="pr-3 text-left">Code</th><th className="pr-3 text-right">Units</th><th className="pr-3 text-left">Minutes</th><th className="pr-3 text-left">Visits</th><th className="text-right">Charge</th></tr>
          </thead>
          <tbody>
            {(claim.lines || []).map((l) => (
              <tr key={l.line} className="border-t border-slate-100">
                <td className="py-1 pr-3">{ClaimsService.formatDate(l.dateOfService)}</td>
                {form === 'UB04' && <td className="pr-3">{l.revenueCode || '—'}</td>}
                <td className="pr-3">{[l.procedureCode, ...l.modifiers].join(' ')}</td>
                <td className="pr-3 text-right">{l.units}</td>
                <td className="pr-3 text-slate-500">{l.minutes ?? '—'} {l.minutesSource === 'EVV' ? '(HHAX)' : '(Pelora)'}</td>
                <td className="pr-3 text-slate-500">
                  {(l.visitIds?.length ?? 1)}
                  {l.portions?.some((p) => p.portion !== 'FULL') ? ' · split at midnight' : ''}
                </td>
                <td className="text-right">{ClaimsService.formatCurrency(l.charges)}</td>
              </tr>
            ))}
            <tr className="border-t border-slate-200 font-medium">
              <td className="py-1" colSpan={form === 'UB04' ? 3 : 2}>Total</td>
              <td className="pr-3 text-right">{claim.totalUnits}</td>
              <td />
              <td />
              <td className="text-right">{ClaimsService.formatCurrency(claim.totalAmount)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <dl className="mt-3 grid grid-cols-1 gap-x-4 gap-y-1 text-sm sm:grid-cols-2">
        {claim.submissionDate && (<div><dt className="inline text-slate-500">Submitted: </dt><dd className="inline">{ClaimsService.formatDate(claim.submissionDate)} ({claim.submissionMethod})</dd></div>)}
        {claim.paymentDate && (<div><dt className="inline text-slate-500">Paid: </dt><dd className="inline">{ClaimsService.formatDate(claim.paymentDate)} · {ClaimsService.formatCurrency(claim.paymentAmount)}</dd></div>)}
        {claim.denialReason && (<div><dt className="inline text-slate-500">Denial reason: </dt><dd className="inline">{claim.denialReasonCode ? `${claim.denialReasonCode} · ` : ''}{claim.denialReason}{claim.remarkCodes?.length ? ` (remarks ${claim.remarkCodes.join(', ')})` : ''}</dd></div>)}
        {claim.appealBy && (claim.status === 'DENIED' || claim.status === 'REJECTED') && (
          <div><dt className="inline text-slate-500">Correct or appeal by: </dt><dd className="inline font-medium text-red-700">{ClaimsService.formatDate(claim.appealBy)}</dd></div>
        )}
        {claim.claimRule?.submitTo === 'TMHP' && (<div><dt className="inline text-slate-500">Submit to: </dt><dd className="inline">TMHP (TexMedConnect)</dd></div>)}
        {claim.rejectionReason && (<div><dt className="inline text-slate-500">Rejection reason: </dt><dd className="inline">{claim.rejectionReason}</dd></div>)}
        <div>
          <dt className="inline text-slate-500">Secondary billed: </dt>
          <dd className="inline">{claim.secondaryBilled === 'Y' ? 'Yes' : claim.secondaryBilled === 'N' ? 'No' : claim.secondaryBilled === 'NA' ? 'No secondary' : 'Not set'}{claim.secondaryNote ? ` — ${claim.secondaryNote}` : ''}</dd>
        </div>
        {claim.paSummary && (<div><dt className="inline text-slate-500">Prior auth: </dt><dd className="inline">{claim.paSummary.authorizationNumber} ({claim.paSummary.status})</dd></div>)}
      </dl>

      {claim.versions?.length > 1 && (
        <div className="mt-3">
          <h4 className="text-xs font-semibold uppercase text-slate-500">Versions</h4>
          <ul className="mt-1 flex flex-wrap gap-2 text-sm">
            {claim.versions.map((v) => (
              <li key={v.claimId}>
                <button type="button" disabled={v.claimId === claim.claimId} onClick={() => onOpenVersion(v.claimId)} className="rounded border border-slate-300 px-2 py-0.5 disabled:bg-slate-100">
                  v{v.version} · {v.status}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        {claim.hasDocument && (
          <button type="button" onClick={onDownload} className="flex items-center gap-1 rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-700">
            <Download size={14} /> {form === 'UB04' ? 'UB-04 keying sheet (PDF)' : 'CMS-1500 PDF'}
          </button>
        )}
        <button type="button" onClick={() => setShowWorksheet((v) => !v)} className="flex items-center gap-1 rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-700">
          <ClipboardList size={14} /> {showWorksheet ? 'Hide worksheet' : 'Keying worksheet'}
        </button>
        {(canChangeStatus || canUpdate) && claim.status !== 'VOID' && claim.status !== 'CORRECTED' && (
          <button type="button" onClick={onUpdateStatus} className="rounded-md bg-slate-800 px-3 py-1.5 text-sm text-white">Update status</button>
        )}
        {correctable && <button type="button" onClick={onCorrect} className="rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-700">Corrected claim</button>}
        {voidable && <button type="button" onClick={onVoid} className="rounded-md border border-red-300 px-3 py-1.5 text-sm text-red-700">Void</button>}
      </div>

      {showWorksheet && (
        <div className="mt-4">
          <ClaimWorksheetPanel claimId={claim.claimId} onClose={() => setShowWorksheet(false)} />
        </div>
      )}

      <div className="mt-4">
        <h4 className="mb-2 text-xs font-semibold uppercase text-slate-500">History</h4>
        <ClaimStatusHistory history={claim.statusHistory} />
      </div>
    </section>
  );
};

export default ClaimDetailPanel;
